import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../utils/alert";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  // Modal States
  isModalOpen: false,
  isSubmitting: false,

  // Edit States
  editMode: false,
  editId: null,

  // Data Storage
  costCentersList: [],

  // Form Payload
  form: {
    name: "",
    description: "",
  },

  // DataTable Instance
  dataTableInstance: null,

  async initCostCenters() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Chama diretamente a rota do recurso.
      // Se faltar permissão ou feature no plano, o backend retornará erro 403
      await this.loadTable();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  // ================= FORMATTING HELPERS =================
  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  },

  // ================= DATA TABLE =================
  // Removido o try/catch para que o erro suba para o initCostCenters e o 403 seja processado.
  async loadTable() {
    const response = await axios.get("/api/client/financial/cost-centers");
    this.costCentersList = response.data.data;

    if (this.dataTableInstance) {
      this.dataTableInstance.destroy();
    }

    const tbody = document.querySelector("#costCentersTable tbody");
    if (!tbody) return;

    tbody.innerHTML = this.costCentersList
      .map((center) => {
        return `
          <tr class="hover:bg-slate-50/50 transition-colors">
            <td class="py-4 px-4 xl:px-6">
              <p class="font-bold text-slate-800">${center.name}</p>
            </td>
            <td class="py-4 px-4 xl:px-6 text-slate-500 text-sm truncate max-w-xs">
              ${center.description || "-"}
            </td>
            <td class="py-4 px-4 xl:px-6 text-slate-500 text-sm">
              ${this.formatDate(center.created_at)}
            </td>
            <td class="text-left py-4 px-4 xl:px-6">
              <div class="flex justify-start gap-2">
                <button
                  onclick="window.dispatchEvent(new CustomEvent('edit-costcenter', {detail: ${center.id}}))"
                  class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  Editar
                </button>
                <button
                  onclick="window.dispatchEvent(new CustomEvent('delete-costcenter', {detail: ${center.id}}))"
                  class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600"
                >
                  Excluir
                </button>
              </div>
            </td>
          </tr>
        `;
      })
      .join("");

    this.dataTableInstance = new DataTable("#costCentersTable", {
      searchable: false,
      perPageSelect: false,
      labels: {
        info: "Mostrando de {start} até {end} de {rows} centros de custo",
        noRows: "Nenhum centro de custo cadastrado",
        noResults: "Nenhum resultado encontrado",
        searchTitle: "Buscar...",
      },
    });
  },

  // ================= MODAL HANDLERS =================
  openCreate() {
    this.editMode = false;
    this.form = {
      name: "",
      description: "",
    };
    this.isModalOpen = true;
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;
    const center = this.costCentersList.find((c) => c.id === id);

    this.form = {
      name: center.name,
      description: center.description || "",
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================
  async submitForm() {
    if (!this.form.name || this.form.name.trim() === "") {
      showAlert("Por favor, preencha o Nome do Centro de Custo.", "warning");
      return;
    }

    this.isSubmitting = true;
    try {
      if (this.editMode) {
        await axios.put(
          `/api/client/financial/cost-centers/${this.editId}`,
          this.form,
        );
        showAlert("Centro de custo atualizado com sucesso!", "success");
      } else {
        await axios.post("/api/client/financial/cost-centers", this.form);
        showAlert("Centro de custo cadastrado com sucesso!", "success");
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 422 &&
        error.response.data.error
      ) {
        showAlert(error.response.data.error, "warning");
      } else {
        handleRequestError(error, "Erro ao guardar o centro de custo.");
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= DELETE =================
  async deleteRecord(id) {
    const result = await showConfirm(
      "Excluir Centro de Custo",
      "Tem certeza? O histórico financeiro será mantido, mas este item não aparecerá mais para novos lançamentos.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/financial/cost-centers/${id}`);
        await this.loadTable();
        showAlert("Centro de custo removido com sucesso.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir o centro de custo.");
      }
    }
  },
});
